import React from 'react';
import {
    Visibility,
    TrendingUp,
    TrendingDown,
    Payment,
    QrCode,
    Receipt,
    Link,
    SwapHoriz
} from '@mui/icons-material';
import { transactionStatusLabel } from '../../../utils/transactionDetail';

const TransactionTable = ({ transactions, onTransactionSelect, type, currentPage = 1, nextPage = null, prevPage = null, onPageChange, showCaisse = false }) => {
    const getTypeIcon = (transaction) => {
        switch (transaction.type) {
            case 'deposit': return <TrendingUp className="text-blue-600" />;
            case 'withdrawal': return <TrendingDown className="text-amber-600" />;
            case 'payment': return <Payment className="text-purple-600" />;
            case 'invoice': return <Receipt className="text-orange-600" />;
            case 'paymentRequest': return <QrCode className="text-green-600" />;
            case 'paymentLink': return <Link className="text-cyan-600" />;
            case 'transfer': return <SwapHoriz className="text-indigo-600" />;
            default: return <Payment className="text-gray-600" />;
        }
    };

    const getTypeColor = (transaction) => {
        switch (transaction.type) {
            case 'deposit': return 'bg-blue-100 text-blue-800';
            case 'withdrawal': return 'bg-amber-100 text-amber-800';
            case 'payment': return 'bg-purple-100 text-purple-800';
            case 'invoice': return 'bg-orange-100 text-orange-800';
            case 'paymentRequest': return 'bg-green-100 text-green-800';
            case 'paymentLink': return 'bg-cyan-100 text-cyan-800';
            case 'transfer': return 'bg-indigo-100 text-indigo-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'bg-green-100 text-green-800 border border-green-200';
            case 'pending': return 'bg-amber-100 text-amber-800 border border-amber-200';
            case 'failed': return 'bg-red-100 text-red-800 border border-red-200';
            case 'expired': return 'bg-gray-100 text-gray-800 border border-gray-200';
            default: return 'bg-gray-100 text-gray-800 border border-gray-200';
        }
    };

    if (!transactions || transactions.length === 0) {
        return (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-12 text-center">
                <Payment className="text-gray-400 text-4xl mx-auto mb-4" />
                <h3 className="text-gray-500 text-lg font-medium">Aucune donnée disponible</h3>
                <p className="text-gray-400 text-sm mt-1">
                    Aucune transaction ne correspond à vos critères.
                </p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-bold text-gray-900">
                    {type === 'all' && 'Toutes les Transactions'}
                    {type === 'paymentRequests' && 'Demandes de Paiement'}
                    {type === 'invoices' && 'Factures'}
                    {type === 'paymentLinks' && 'Liens de Paiement'}
                    <span className="text-sm font-normal text-gray-500 ml-2">
                        ({transactions.length} résultat{transactions.length > 1 ? 's' : ''})
                    </span>
                </h3>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            {type === 'all' ? 'Transaction' : 'Référence'}
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Client
                        </th>
                        {showCaisse && (
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Caisse
                        </th>
                        )}
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Montant
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Statut
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Date
                        </th>
                        <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Actions
                        </th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                    {transactions.map((transaction) => (
                        <tr
                            key={transaction.id}
                            className="hover:bg-gray-50 transition-colors cursor-pointer"
                            onClick={() => onTransactionSelect(transaction)}
                        >
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gray-100">
                                        {getTypeIcon(transaction)}
                                    </div>
                                    <div>
                                        <div className="font-semibold text-gray-900">
                                            {transaction.reference || transaction.transactionId}
                                        </div>
                                        <div className="flex items-center space-x-2 mt-1">
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(transaction)}`}>
                                                    {transaction.typeLabel || transaction.type}
                                                </span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                    {transaction.customer}
                                </div>
                                <div className="text-sm text-gray-500">
                                    {transaction.description}
                                </div>
                            </td>
                            {showCaisse && (
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                    {transaction.caisseName || '—'}
                                </div>
                                {transaction.cashierName && transaction.cashierName !== '—' && (
                                    <div className="text-xs text-gray-500">
                                        {transaction.cashierName}
                                    </div>
                                )}
                            </td>
                            )}
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-bold text-gray-900">
                                    {transaction.amount?.toLocaleString()} {transaction.currency}
                                </div>
                                {transaction.netAmount && (
                                    <div className="text-xs text-gray-500">
                                        Net: {transaction.netAmount.toLocaleString()} {transaction.currency}
                                    </div>
                                )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(transaction.status)}`}>
                                        {transactionStatusLabel(transaction.status)}
                                    </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {transaction.date}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button className="text-blue-600 hover:text-blue-900 p-2 rounded-lg hover:bg-blue-50 transition-colors">
                                    <Visibility />
                                </button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination bidirectionnelle (comme sur mobile) */}
            {(nextPage || prevPage) && (
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600">
                            Page {currentPage} - {transactions.length} transaction(s)
                        </div>
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => {
                                    if (prevPage && prevPage < currentPage) {
                                        onPageChange?.(prevPage);
                                    }
                                }}
                                disabled={!prevPage || prevPage >= currentPage}
                                className="px-4 py-2 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                            >
                                ← Précédent
                            </button>
                            <span className="px-4 py-2 text-sm text-gray-600">
                                {currentPage}
                            </span>
                            <button
                                onClick={() => {
                                    if (nextPage && nextPage > currentPage) {
                                        onPageChange?.(nextPage);
                                    }
                                }}
                                disabled={!nextPage || nextPage <= currentPage}
                                className="px-4 py-2 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                            >
                                Suivant →
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TransactionTable;